import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Invoice, Percent, Headset, Lightning, FileText } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { VehicleFinder } from "../components/VehicleFinder";
import { HeroCarousel } from "../components/HeroCarousel";
import { AudienceBanners, ReviewsSection, BlogSection, FaqSection } from "../components/home/Sections";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1567016958860-87d898933af1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800";

const BRANDS = ["ENEOS", "ORLEN", "PHILIPS", "MOTIP", "POLAR", "HOLTS", "WD-40", "QUIXX", "HI GEAR", "AVIA"];

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/products", { params: { featured: true, limit: 10 } }).then(({ data }) => setFeatured(data.items)).catch(() => {});
  }, []);

  return (
    <div>
      <HeroCarousel />

      <VehicleFinder />

      <section className="border-b border-border bg-white mt-10">
        <div className="mx-auto max-w-7xl grid grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Truck, t: "Piegāde 1–2 dienās", d: "Visā Latvijā, mucas ar autoiekrāvēju" },
            { icon: Percent, t: "Apjoma atlaides", d: "Cenu līmeņi katram produktam" },
            { icon: Invoice, t: "Apmaksa ar rēķinu", d: "B2B pēcapmaksa juridiskām personām" },
            { icon: Headset, t: "Tehniskā konsultācija", d: "Palīdzēsim izvēlēties pareizo eļļu" },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-3 p-5 border-r border-b lg:border-b-0 border-border/60">
              <f.icon size={26} className="shrink-0 text-[#2E86D1]" />
              <div><p className="text-sm font-bold">{f.t}</p><p className="mt-0.5 text-xs text-slate-500">{f.d}</p></div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:py-16">
        <div className="flex items-end justify-between">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Kategorijas</h2>
          <Link to="/katalogs" data-testid="all-categories-link" className="text-sm font-semibold tracking-wide hover:text-[#2E86D1]">Viss katalogs →</Link>
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {categories.map((c) => (
            <Link key={c.id} to={`/katalogs?category=${c.id}`} data-testid={`category-tile-${c.id}`}
              className="card-industrial group relative block aspect-[4/3] overflow-hidden border border-border">
              <img src={c.image} alt={c.name} loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c1a28]/90 via-[#0c1a28]/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-5">
                <div>
                  <p className="font-heading text-lg font-bold leading-tight text-white">{c.name}</p>
                  <p className="mt-0.5 text-xs text-blue-100/80">{c.count} produkti</p>
                </div>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#2E86D1] text-white transition-colors group-hover:bg-white group-hover:text-[#2E86D1]">
                  <ArrowRight size={18} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <AudienceBanners />

      <section className="mx-auto max-w-7xl px-4">
        <div className="rounded-3xl bg-[#0c1a28] text-white overflow-hidden grid grid-cols-1 md:grid-cols-2 gap-10 items-center px-6 py-12 md:px-14 md:py-16">
          <div>
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#8FC1EA]">B2B un lauksaimniekiem</p>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Eļļas mucās un vairumā — izdevīgāk</h2>
            <ul className="mt-6 space-y-3 text-sm text-slate-300">
              <li className="flex gap-2"><span className="text-[#8FC1EA] font-bold">01</span> Motoreļļas, hidraulikas eļļas un AdBlue 20L–1000L fasējumos</li>
              <li className="flex gap-2"><span className="text-[#8FC1EA] font-bold">02</span> Individuālas atlaides pastāvīgajiem B2B klientiem</li>
              <li className="flex gap-2"><span className="text-[#8FC1EA] font-bold">03</span> Ātrā pasūtīšana pēc artikula numura — bez meklēšanas</li>
              <li className="flex gap-2"><span className="text-[#8FC1EA] font-bold">04</span> Apmaksa ar rēķinu un piegāde uz saimniecību vai servisu</li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/atra-pasutisana" data-testid="b2b-quickorder-btn" className="btn-industrial inline-flex items-center gap-2 bg-[#2E86D1] px-5 py-3 text-sm font-semibold tracking-wide hover:bg-[#1F6DB2]">
                <Lightning size={18} weight="fill" /> Ātrā pasūtīšana
              </Link>
              <Link to="/pieteikties?b2b=1" data-testid="b2b-register-btn" className="btn-industrial inline-flex items-center gap-2 border border-white px-5 py-3 text-sm font-semibold tracking-wide hover:bg-white hover:text-[#1D1D1F]">
                <FileText size={18} /> B2B reģistrācija
              </Link>
            </div>
          </div>
          <img src="https://images.unsplash.com/photo-1567016958860-87d898933af1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200" alt="Eļļas mucas noliktavā" loading="lazy" className="hidden md:block h-80 w-full rounded-2xl object-cover" />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:py-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Populārākie produkti</h2>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {featured.map((p) => <ProductCard key={p.sku} product={p} />)}
        </div>
      </section>

      <ReviewsSection />
      <BlogSection />
      <FaqSection />

      <section className="border-t border-border bg-[#F4F5F7]">
        <div className="mx-auto max-w-7xl px-4 py-10">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 text-center">Piedāvājam vadošos zīmolus</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {BRANDS.map((b) => (
              <span key={b} className="font-heading text-lg md:text-xl font-black uppercase tracking-tight text-slate-400">{b}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
